import React, { createContext, useContext, useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Link, useNavigate, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { 
  Anchor, Ship, Wrench, Package, FileText, Clock, CheckCircle, 
  ChevronRight, Menu, X, User, LogOut, Plus, Upload, Search,
  Phone, Mail, MapPin, ArrowRight, Settings, Gauge, Box, Filter,
  Calculator, Trash2, Edit, DollarSign, Lock, Save, Users, Eye
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { TeamAdminProvider, TeamAdminLoginPage, TeamAdminDashboard } from "./AdminPanel";
import "@/App.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const isProduction = window.location.hostname.includes('spoolfix') || window.location.hostname.includes('emergent.host');
const API = isProduction ? "/api" : `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

// Admin Context
const AdminContext = createContext(null);

export const useAuth = () => useContext(AuthContext);
export const useAdmin = () => useContext(AdminContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const res = await axios.get(`${API}/auth/me`);
      setUser(res.data);
    } catch (e) {
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem("token", res.data.access_token);
    setToken(res.data.access_token);
    setUser(res.data.user);
    axios.defaults.headers.common["Authorization"] = `Bearer ${res.data.access_token}`;
    return res.data;
  };

  const register = async (data) => {
    const res = await axios.post(`${API}/auth/register`, data);
    localStorage.setItem("token", res.data.access_token);
    setToken(res.data.access_token);
    setUser(res.data.user);
    axios.defaults.headers.common["Authorization"] = `Bearer ${res.data.access_token}`;
    return res.data;
  };

  const logout = () => {
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common["Authorization"];
  };

  return (
    <AuthContext.Provider value={{ user, token, login, register, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

const AdminProvider = ({ children }) => {
  const [adminToken, setAdminToken] = useState(localStorage.getItem("adminToken"));
  const [isAdmin, setIsAdmin] = useState(!!localStorage.getItem("adminToken"));

  const adminLogin = async (password) => {
    const res = await axios.post(`${API}/admin/login`, { password });
    localStorage.setItem("adminToken", res.data.access_token);
    setAdminToken(res.data.access_token);
    setIsAdmin(true);
    return res.data;
  };

  const adminLogout = () => {
    localStorage.removeItem("adminToken");
    setAdminToken(null);
    setIsAdmin(false);
  };

  const getAdminHeaders = () => ({
    Authorization: `Bearer ${adminToken}`
  });

  return (
    <AdminContext.Provider value={{ isAdmin, adminToken, adminLogin, adminLogout, getAdminHeaders }}>
      {children}
    </AdminContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  return children;
};

// Loading Screen
const LoadingScreen = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <div className="text-center">
      <Anchor className="w-12 h-12 text-primary animate-pulse mx-auto mb-4" />
      <p className="text-muted-foreground">Loading...</p>
    </div>
  </div>
);

// Navigation
const Navigation = () => {
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/");
    toast.success("Logged out successfully");
  };

  return (
    <nav className="nav-glass sticky top-0 z-50" data-testid="main-navigation">
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
            <img src="/logo-v2.png" alt="SpoolFix" className="h-24" />
          </Link>
          
          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            <Link to="/quote-calculator" className="nav-link text-primary hover:text-primary/80 transition-colors font-medium" data-testid="nav-quote">Get Quote</Link>
            <Link to="/services" className="nav-link text-muted-foreground hover:text-foreground transition-colors" data-testid="nav-services">Services</Link>
            <Link to="/materials" className="nav-link text-muted-foreground hover:text-foreground transition-colors" data-testid="nav-materials">Materials</Link>
            <Link to="/how-it-works" className="nav-link text-muted-foreground hover:text-foreground transition-colors" data-testid="nav-how-it-works">How It Works</Link>
            <Link to="/about" className="nav-link text-muted-foreground hover:text-foreground transition-colors" data-testid="nav-about">About</Link>
            <Link to="/contact" className="nav-link text-muted-foreground hover:text-foreground transition-colors" data-testid="nav-contact">Contact</Link>
          </div>

          <div className="hidden md:flex items-center gap-4">
            {user ? (
              <>
                <Link to="/dashboard">
                  <Button variant="ghost" className="gap-2" data-testid="nav-dashboard">
                    <Gauge className="w-4 h-4" />
                    Dashboard
                  </Button>
                </Link>
                <Button variant="outline" onClick={handleLogout} className="gap-2" data-testid="nav-logout">
                  <LogOut className="w-4 h-4" />
                  Logout
                </Button>
              </>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" data-testid="nav-login">Login</Button>
                </Link>
                <Link to="/register">
                  <Button className="btn-glow" data-testid="nav-register">Get Started</Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <button 
            className="md:hidden p-2" 
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Nav */}
        {mobileOpen && (
          <div className="md:hidden pt-4 pb-2 border-t border-border mt-4">
            <div className="flex flex-col gap-2">
              <Link to="/quote-calculator" className="py-2 text-primary font-medium" onClick={() => setMobileOpen(false)}>Get Quote</Link>
              <Link to="/services" className="py-2 text-muted-foreground" onClick={() => setMobileOpen(false)}>Services</Link>
              <Link to="/materials" className="py-2 text-muted-foreground" onClick={() => setMobileOpen(false)}>Materials</Link>
              <Link to="/how-it-works" className="py-2 text-muted-foreground" onClick={() => setMobileOpen(false)}>How It Works</Link>
              <Link to="/about" className="py-2 text-muted-foreground" onClick={() => setMobileOpen(false)}>About</Link>
              <Link to="/contact" className="py-2 text-muted-foreground" onClick={() => setMobileOpen(false)}>Contact</Link>
              <div className="pt-4 border-t border-border flex flex-col gap-2">
                {user ? (
                  <>
                    <Link to="/dashboard" onClick={() => setMobileOpen(false)}>
                      <Button variant="ghost" className="w-full justify-start gap-2">
                        <Gauge className="w-4 h-4" /> Dashboard
                      </Button>
                    </Link>
                    <Button variant="outline" onClick={() => { handleLogout(); setMobileOpen(false); }} className="gap-2">
                      <LogOut className="w-4 h-4" /> Logout
                    </Button>
                  </>
                ) : (
                  <>
                    <Link to="/login" onClick={() => setMobileOpen(false)}>
                      <Button variant="ghost" className="w-full">Login</Button>
                    </Link>
                    <Link to="/register" onClick={() => setMobileOpen(false)}>
                      <Button className="w-full btn-glow">Get Started</Button>
                    </Link>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

// Footer
const Footer = () => {
  const [visitorCount, setVisitorCount] = useState(null);
  
  useEffect(() => {
    axios.get(`${API}/visitor-count`).then(res => {
      setVisitorCount(res.data.total_visits);
    }).catch(() => {});
  }, []);

  return (
  <footer className="bg-card border-t border-border mt-auto">
    <div className="max-w-7xl mx-auto px-6 py-12">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
        <div>
          <div className="mb-4">
            <img src="/logo-v2.png" alt="SpoolFix" className="h-12" />
          </div>
          <p className="text-muted-foreground text-sm">
            Your reliable partner for industrial pipe spool fabrication and supply solutions.
          </p>
        </div>
        <div>
          <h4 className="font-semibold mb-4">Services</h4>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li><Link to="/services" className="hover:text-foreground transition-colors">Spool Fabrication</Link></li>
            <li><Link to="/materials" className="hover:text-foreground transition-colors">Raw Materials</Link></li>
            <li><Link to="/how-it-works" className="hover:text-foreground transition-colors">Process</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="font-semibold mb-4">Company</h4>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li><Link to="/about" className="hover:text-foreground transition-colors">About Us</Link></li>
            <li><Link to="/contact" className="hover:text-foreground transition-colors">Contact</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="font-semibold mb-4">Contact</h4>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li className="flex items-center gap-2"><Mail className="w-4 h-4" /> info@baltixa.com</li>
            <li className="flex items-center gap-2"><Phone className="w-4 h-4" /> +90 533 243 53 24</li>
            <li className="flex items-center gap-2"><MapPin className="w-4 h-4" /> Turkey</li>
          </ul>
        </div>
      </div>
      <div className="border-t border-border mt-8 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
        <div className="flex flex-col items-center md:items-start gap-2">
          <p className="text-sm text-muted-foreground">© {new Date().getFullYear()} SpoolFix. All rights reserved.</p>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span>SpoolFix is a brand of</span>
            <a href="https://www.baltixa.com" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 hover:opacity-80 transition-opacity">
              <img src="/baltixa-logo.png" alt="Baltixa Engineering" className="h-6 inline-block" />
            </a>
          </div>
        </div>
        {visitorCount !== null && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="footer-visitor-counter">
            <Eye className="w-4 h-4 text-sky-400" />
            <span>Total Visitors: <span className="text-sky-400 font-semibold">{visitorCount.toLocaleString()}</span></span>
          </div>
        )}
        <Link 
          to="/team-admin/login" 
          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-sky-400 transition-colors"
          data-testid="team-admin-login-btn"
        >
          <Users className="w-4 h-4" />
          Spoolfix Team Login
        </Link>
      </div>
    </div>
  </footer>
  );
};

// WhatsApp Floating Button
const WhatsAppButton = () => (
  <a
    href="https://wa.me/905332435324"
    target="_blank"
    rel="noopener noreferrer"
    className="fixed bottom-6 right-6 z-50 bg-green-500 hover:bg-green-600 text-white p-4 rounded-full shadow-lg transition-all duration-300 hover:scale-110 flex items-center gap-2 group"
    data-testid="whatsapp-button"
  >
    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
    </svg>
    <span className="max-w-0 overflow-hidden group-hover:max-w-xs transition-all duration-300 whitespace-nowrap">
      Chat with us
    </span>
  </a>
);

// ===================== PUBLIC PAGES =====================

const HomePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    // Seed database on load
    axios.post(`${API}/seed`).catch(() => {});
    axios.post(`${API}/seed-pricing`).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://customer-assets.emergentagent.com/job_1cca8699-d906-4332-b3fe-4b6af9beae1b/artifacts/j8nak8wx_image.png" 
            alt="Industrial Pipe Systems"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background/90 via-background/50 to-transparent"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-6 py-24">
          <div className="max-w-2xl animate-fade-in-up">
            <Badge className="mb-6 bg-primary/20 text-primary border-primary/30 uppercase tracking-wider">
              Industrial Pipe Solutions
            </Badge>
            <h1 className="text-5xl md:text-7xl font-black mb-6 leading-tight">
              Pipe Spool<br />
              <span className="text-primary">Fabrication</span>
            </h1>
            <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
              From sketch to site. Custom pipe spool fabrication for maritime, 
              industrial plants, shipyards, and refineries. Precision-welded, delivered worldwide.
            </p>
            <div className="flex flex-wrap gap-4">
              <Button 
                size="lg" 
                className="btn-glow gap-2 text-base"
                onClick={() => navigate("/quote-calculator")}
                data-testid="hero-cta"
              >
                <Calculator className="w-5 h-5" /> Get Instant Quote
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="gap-2 text-base"
                onClick={() => navigate(user ? "/dashboard/new-order" : "/register")}
                data-testid="hero-start-order"
              >
                Start Your Order <ArrowRight className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Quote CTA */}
      <section className="py-12 bg-primary/10 border-y border-primary/20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <Calculator className="w-12 h-12 text-primary" />
              <div>
                <h3 className="text-xl font-bold">Instant Quote Calculator</h3>
                <p className="text-muted-foreground">Get preliminary pricing for your spool fabrication project</p>
              </div>
            </div>
            <Button onClick={() => navigate("/quote-calculator")} className="btn-glow gap-2" data-testid="quick-quote-btn">
              Calculate Now <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-24 grid-texture">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Our Services</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Comprehensive pipe solutions for maritime, industrial, and energy sectors
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            <Card className="card-industrial bg-card border-border hover:border-primary/50 transition-all duration-300 group cursor-pointer" onClick={() => navigate("/services")} data-testid="service-fabrication-card">
              <CardContent className="p-8">
                <div className="w-16 h-16 rounded-sm bg-primary/10 flex items-center justify-center mb-6 group-hover:bg-primary/20 transition-colors">
                  <Wrench className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-2xl font-bold mb-4">Spool Fabrication</h3>
                <p className="text-muted-foreground mb-6">
                  Custom pipe spool fabrication for any industry. From hand sketches to engineered drawings, 
                  we deliver precision-welded spools ready for installation.
                </p>
                <div className="flex items-center text-primary font-medium">
                  Learn more <ChevronRight className="w-5 h-5 ml-1" />
                </div>
              </CardContent>
            </Card>
            <Card className="card-industrial bg-card border-border hover:border-primary/50 transition-all duration-300 group cursor-pointer" onClick={() => navigate("/materials")} data-testid="service-materials-card">
              <CardContent className="p-8">
                <div className="w-16 h-16 rounded-sm bg-primary/10 flex items-center justify-center mb-6 group-hover:bg-primary/20 transition-colors">
                  <Package className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-2xl font-bold mb-4">Raw Materials Library</h3>
                <p className="text-muted-foreground mb-6">
                  Access our comprehensive catalog of pipes, flanges, elbows, and valves. 
                  Quality materials meeting international industry standards, ready for order.
                </p>
                <div className="flex items-center text-primary font-medium">
                  Browse catalog <ChevronRight className="w-5 h-5 ml-1" />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Industries We Serve */}
      <section className="py-24 bg-card">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Industries We Serve</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Trusted by leading companies across multiple sectors
            </p>
          </div>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { icon: Ship, title: "Maritime", desc: "Vessels & Ports" },
              { icon: Wrench, title: "Shipyards", desc: "New Build & Repair" },
              { icon: Settings, title: "Refineries", desc: "Oil & Gas" },
              { icon: Package, title: "Industrial", desc: "Plants & Facilities" }
            ].map((item, i) => (
              <Card key={i} className="bg-card border-border hover:border-primary/50 transition-all text-center p-6">
                <item.icon className="w-10 h-10 text-primary mx-auto mb-4" />
                <h4 className="font-bold text-lg mb-1">{item.title}</h4>
                <p className="text-muted-foreground text-sm">{item.desc}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Process Preview */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">How It Works</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Simple process from inquiry to delivery
            </p>
          </div>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { icon: FileText, title: "Submit", desc: "Upload your sketch or drawing" },
              { icon: Settings, title: "Review", desc: "Our team reviews and confirms" },
              { icon: Wrench, title: "Fabricate", desc: "Precision manufacturing" },
              { icon: Ship, title: "Deliver", desc: "Ship to your location" }
            ].map((step, i) => (
              <div key={i} className="relative text-center p-6">
                <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4 relative z-10">
                  <step.icon className="w-10 h-10 text-primary" />
                  <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold">
                    {i + 1}
                  </div>
                </div>
                <h4 className="font-bold text-lg mb-2">{step.title}</h4>
                <p className="text-muted-foreground text-sm">{step.desc}</p>
                {i < 3 && <div className="process-connector" />}
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Button onClick={() => navigate("/how-it-works")} variant="outline" className="gap-2">
              View Full Process <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-primary/10 to-transparent"></div>
        <div className="max-w-7xl mx-auto px-6 relative">
          <div className="max-w-xl">
            <h2 className="text-4xl font-bold mb-4">Ready to Get Started?</h2>
            <p className="text-muted-foreground mb-8">
              Create your account and submit your first order today. Our team is ready to assist you.
            </p>
            <Button 
              size="lg" 
              className="btn-glow gap-2"
              onClick={() => navigate(user ? "/dashboard" : "/register")}
              data-testid="cta-get-started"
            >
              {user ? "Go to Dashboard" : "Create Account"} <ArrowRight className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

const ServicesPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  return (
    <div className="min-h-screen py-24" data-testid="services-page">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <Badge className="mb-4 bg-primary/20 text-primary border-primary/30 uppercase tracking-wider">
            Our Services
          </Badge>
          <h1 className="text-5xl font-bold mb-6">Pipe Solutions</h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Comprehensive pipe fabrication and material supply for all industries
          </p>
        </div>

        {/* Spool Fabrication Service */}
        <div className="grid lg:grid-cols-2 gap-12 items-center mb-24">
          <div>
            <div className="w-16 h-16 rounded-sm bg-primary/10 flex items-center justify-center mb-6">
              <Wrench className="w-8 h-8 text-primary" />
            </div>
            <h2 className="text-3xl font-bold mb-4">Pipe Spool Fabrication</h2>
            <p className="text-muted-foreground mb-6 leading-relaxed">
              Our core service provides custom pipe spool fabrication for maritime vessels, industrial plants, shipyards, and refineries. 
              Whether you have a simple hand sketch or detailed engineering drawings, our team transforms 
              your requirements into precision-fabricated spools.
            </p>
            <ul className="space-y-3 mb-8">
              {[
                "Accept hand sketches and CAD drawings",
                "24-hour technical review and quotation",
                "Quality-controlled fabrication process",
                "Worldwide delivery to any location",
                "Material certifications included"
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 shrink-0" />
                  <span className="text-muted-foreground">{item}</span>
                </li>
              ))}
            </ul>
            <Button 
              className="btn-glow gap-2"
              onClick={() => navigate(user ? "/dashboard/new-order" : "/register")}
              data-testid="fabrication-order-btn"
            >
              Request Fabrication <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
          <div className="relative">
            <img 
              src="https://customer-assets.emergentagent.com/job_1cca8699-d906-4332-b3fe-4b6af9beae1b/artifacts/3ep5nidm_image.png"
              alt="Pipe Spool Welding"
              className="rounded-sm shadow-2xl"
            />
          </div>
        </div>

        {/* Industries We Serve */}
        <div className="my-24">
          <h2 className="text-3xl font-bold mb-8 text-center">Industries We Serve</h2>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { title: "Maritime", items: ["Vessel maintenance", "Port facilities", "Offshore platforms"] },
              { title: "Shipyards", items: ["New construction", "Repair & retrofit", "Dry dock services"] },
              { title: "Oil & Gas", items: ["Refineries", "Petrochemical", "Pipeline systems"] },
              { title: "Industrial", items: ["Power plants", "Manufacturing", "Process facilities"] }
            ].map((sector, i) => (
              <Card key={i} className="bg-card border-border p-6">
                <h3 className="font-bold text-lg mb-3 text-primary">{sector.title}</h3>
                <ul className="space-y-2">
                  {sector.items.map((item, j) => (
                    <li key={j} className="flex items-center gap-2 text-sm text-muted-foreground">
                      <CheckCircle className="w-4 h-4 text-green-500 shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </Card>
            ))}
          </div>
        </div>

        {/* Raw Materials Service */}
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="order-2 lg:order-1">
            <img 
              src="https://images.pexels.com/photos/15508177/pexels-photo-15508177.jpeg?w=800"
              alt="Industrial Steel Pipes"
              className="rounded-sm shadow-2xl"
            />
          </div>
          <div className="order-1 lg:order-2">
            <div className="w-16 h-16 rounded-sm bg-accent/10 flex items-center justify-center mb-6">
              <Package className="w-8 h-8 text-accent" />
            </div>
            <h2 className="text-3xl font-bold mb-4">Raw Materials Library</h2>
            <p className="text-muted-foreground mb-6 leading-relaxed">
              Access our comprehensive inventory of industrial-grade pipe fittings and materials. 
              From standard carbon steel to exotic alloys, we stock materials that meet the 
              demanding requirements of all industrial applications.
            </p>
            <div className="grid grid-cols-2 gap-4 mb-8">
              {[
                { icon: Box, label: "Pipes", count: "50+ types" },
                { icon: Settings, label: "Flanges", count: "30+ types" },
                { icon: Wrench, label: "Elbows", count: "25+ types" },
                { icon: Gauge, label: "Valves", count: "40+ types" }
              ].map((item, i) => (
                <div key={i} className="bg-card border border-border rounded-sm p-4">
                  <item.icon className="w-6 h-6 text-primary mb-2" />
                  <p className="font-semibold">{item.label}</p>
                  <p className="text-sm text-muted-foreground">{item.count}</p>
                </div>
              ))}
            </div>
            <Button 
              variant="outline"
              className="gap-2"
              onClick={() => navigate("/materials")}
              data-testid="materials-browse-btn"
            >
              Browse Materials <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const MaterialsPage = () => {
  const [materials, setMaterials] = useState([]);
  const [category, setCategory] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMaterials();
  }, [category]);

  const fetchMaterials = async () => {
    try {
      const params = new URLSearchParams();
      if (category && category !== "all") params.append("category", category);
      const res = await axios.get(`${API}/materials?${params}`);
      setMaterials(res.data);
    } catch (e) {
      toast.error("Failed to load materials");
    } finally {
      setLoading(false);
    }
  };

  const filteredMaterials = materials.filter(m => 
    !search || m.name.toLowerCase().includes(search.toLowerCase()) || 
    m.material_type.toLowerCase().includes(search.toLowerCase())
  );

  const categories = [
    { value: "all", label: "All Materials" },
    { value: "pipe", label: "Pipes" },
    { value: "flange", label: "Flanges" },
    { value: "elbow", label: "Elbows" },
    { value: "valve", label: "Valves" }
  ];

  return (
    <div className="min-h-screen py-24" data-testid="materials-page">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-12">
          <Badge className="mb-4 bg-accent/20 text-accent border-accent/30 uppercase tracking-wider">
            Material Library
          </Badge>
          <h1 className="text-5xl font-bold mb-6">Raw Materials</h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Browse our comprehensive catalog of industrial-grade pipe fittings
          </p>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input 
              placeholder="Search materials..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="materials-search"
            />
          </div>
          <Tabs value={category} onValueChange={setCategory} className="w-full md:w-auto">
            <TabsList className="grid grid-cols-5 w-full md:w-auto">
              {categories.map(c => (
                <TabsTrigger key={c.value} value={c.value} data-testid={`filter-${c.value}`}>
                  {c.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>

        {/* Materials Grid */}
        {loading ? (
          <div className="grid md:grid-cols-3 gap-6">
            {[1,2,3,4,5,6].map(i => (
              <Card key={i} className="bg-card animate-pulse">
                <div className="h-48 bg-muted"></div>
                <CardContent className="p-6">
                  <div className="h-6 bg-muted rounded mb-2"></div>
                  <div className="h-4 bg-muted rounded w-2/3"></div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-6">
            {filteredMaterials.map(material => (
              <Card 
                key={material.id} 
                className="material-card bg-card border-border overflow-hidden"
                data-testid={`material-card-${material.id}`}
              >
                <div className="h-48 bg-muted relative">
                  <img 
                    src={material.image_url || "https://images.unsplash.com/photo-1590959651373-a3db0f38a961?w=400"}
                    alt={material.name}
                    className="w-full h-full object-cover"
                  />
                  <Badge className={`absolute top-3 right-3 ${
                    material.stock_status === "in_stock" ? "bg-green-500/20 text-green-400 border-green-500/30" :
                    "bg-yellow-500/20 text-yellow-400 border-yellow-500/30"
                  }`}>
                    {material.stock_status === "in_stock" ? "In Stock" : "Limited"}
                  </Badge>
                </div>
                <CardContent className="p-6">
                  <Badge variant="outline" className="mb-3 uppercase text-xs">
                    {material.category}
                  </Badge>
                  <h3 className="font-bold text-lg mb-2">{material.name}</h3>
                  <p className="text-sm text-muted-foreground mb-3 font-mono">{material.material_type}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Size: {material.size}</span>
                    {material.price_range && (
                      <span className="text-sm font-semibold text-primary">{material.price_range}</span>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {!loading && filteredMaterials.length === 0 && (
          <div className="text-center py-12">
            <Package className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No materials found matching your criteria</p>
          </div>
        )}
      </div>
    </div>
  );
};

const HowItWorksPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const steps = [
    {
      icon: FileText,
      title: "Submit Your Request",
      description: "Upload your spool information - from simple hand sketches to detailed engineering drawings. Our system accepts various file formats.",
      details: ["Hand sketches accepted", "CAD files supported", "PDF drawings welcome", "Photos of existing spools"]
    },
    {
      icon: Clock,
      title: "Technical Review",
      description: "Within 24 hours, our technical department contacts you to review details and clarify any missing information.",
      details: ["24-hour response time", "Expert review", "Clarification of specs", "Quote preparation"]
    },
    {
      icon: Settings,
      title: "Confirmation & Production",
      description: "Once all parameters are confirmed, we proceed with fabrication using quality-controlled processes.",
      details: ["Material procurement", "Precision fabrication", "Quality welding", "Dimensional checks"]
    },
    {
      icon: CheckCircle,
      title: "Quality Control",
      description: "Every spool undergoes rigorous quality control and testing before packaging.",
      details: ["Visual inspection", "Dimensional verification", "Weld testing", "Material certification"]
    },
    {
      icon: Ship,
      title: "Packaging & Delivery",
      description: "Spools are properly packaged and delivered to your targeted destination as planned.",
      details: ["Protective packaging", "Worldwide shipping", "Port delivery", "Tracking provided"]
    }
  ];

  return (
    <div className="min-h-screen py-24" data-testid="how-it-works-page">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <Badge className="mb-4 bg-primary/20 text-primary border-primary/30 uppercase tracking-wider">
            Our Process
          </Badge>
          <h1 className="text-5xl font-bold mb-6">How It Works</h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            A streamlined process from your initial request to final delivery
          </p>
        </div>

        <div className="relative">
          {/* Timeline line */}
          <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-0.5 bg-border -translate-x-1/2"></div>

          {steps.map((step, i) => (
            <div key={i} className={`relative grid lg:grid-cols-2 gap-8 mb-16 ${i % 2 === 1 ? "lg:text-right" : ""}`}>
              <div className={`${i % 2 === 1 ? "lg:order-2" : ""}`}>
                <Card className="card-industrial bg-card border-border p-8">
                  <div className={`flex items-start gap-6 ${i % 2 === 1 ? "lg:flex-row-reverse lg:text-left" : ""}`}>
                    <div className="w-16 h-16 rounded-sm bg-primary/10 flex items-center justify-center shrink-0">
                      <step.icon className="w-8 h-8 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold mb-3">{step.title}</h3>
                      <p className="text-muted-foreground mb-4">{step.description}</p>
                      <ul className="space-y-2">
                        {step.details.map((detail, j) => (
                          <li key={j} className="flex items-center gap-2 text-sm text-muted-foreground">
                            <CheckCircle className="w-4 h-4 text-green-500 shrink-0" />
                            {detail}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </Card>
              </div>
              {/* Step number on timeline */}
              <div className={`hidden lg:flex items-center justify-center ${i % 2 === 1 ? "lg:order-1" : ""}`}>
                <div className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg z-10">
                  {i + 1}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-12">
          <Button 
            size="lg" 
            className="btn-glow gap-2"
            onClick={() => navigate(user ? "/dashboard/new-order" : "/register")}
            data-testid="how-it-works-cta"
          >
            Start Your Order <ArrowRight className="w-5 h-5" />
          </Button>
        </div>
      </div>
    </div>
  );
};

const AboutPage = () => (
  <div className="min-h-screen py-24" data-testid="about-page">
    <div className="max-w-7xl mx-auto px-6">
      <div className="text-center mb-16">
        <Badge className="mb-4 bg-primary/20 text-primary border-primary/30 uppercase tracking-wider">
          About Us
        </Badge>
        <h1 className="text-5xl font-bold mb-6">SpoolFix</h1>
        <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
          Your reliable solution partner for industrial pipe spool fabrication
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-12 items-center mb-24">
        <div>
          <h2 className="text-3xl font-bold mb-6">Our Mission</h2>
          <p className="text-muted-foreground mb-6 leading-relaxed">
            We are a reliable solution partner for pipe spool fabrication across maritime, industrial, and energy sectors. 
            Our system is designed to deliver precision-fabricated spools for maintenance, new construction, 
            and retrofit projects worldwide.
          </p>
          <p className="text-muted-foreground mb-6 leading-relaxed">
            From shipyards to refineries, from vessels to industrial plants - we provide custom pipe spools 
            that meet the highest quality standards and are delivered on time, every time.
          </p>
        </div>
        <div>
          <img 
            src="https://customer-assets.emergentagent.com/job_1cca8699-d906-4332-b3fe-4b6af9beae1b/artifacts/3ep5nidm_image.png"
            alt="Pipe Spool Fabrication"
            className="rounded-sm shadow-2xl"
          />
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-8 mb-24">
        {[
          { value: "500+", label: "Spools Delivered" },
          { value: "100+", label: "Projects Completed" },
          { value: "24h", label: "Response Time" }
        ].map((stat, i) => (
          <Card key={i} className="card-industrial bg-card border-border text-center p-8">
            <p className="text-5xl font-black text-primary mb-2">{stat.value}</p>
            <p className="text-muted-foreground uppercase tracking-wider text-sm">{stat.label}</p>
          </Card>
        ))}
      </div>

      <div className="bg-card rounded-sm p-12 border border-border">
        <h2 className="text-3xl font-bold mb-6 text-center">Why Choose SpoolFix?</h2>
        <div className="grid md:grid-cols-2 gap-8">
          {[
            { title: "Quality Assured", desc: "Every spool is manufactured to industry standards with full material certification." },
            { title: "Fast Turnaround", desc: "From sketch to delivery in minimal time, keeping your operations running." },
            { title: "Global Delivery", desc: "We deliver worldwide to any location - ports, plants, or facilities." },
            { title: "Expert Support", desc: "Our technical team is always available to assist with your requirements." }
          ].map((item, i) => (
            <div key={i} className="flex gap-4">
              <CheckCircle className="w-6 h-6 text-green-500 shrink-0" />
              <div>
                <h4 className="font-bold mb-2">{item.title}</h4>
                <p className="text-muted-foreground">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

const ContactPage = () => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "", email: "", company: "", subject: "", message: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Message sent successfully! We'll get back to you soon.");
      setFormData({ name: "", email: "", company: "", subject: "", message: "" });
    } catch (e) {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen py-24" data-testid="contact-page">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <Badge className="mb-4 bg-primary/20 text-primary border-primary/30 uppercase tracking-wider">
            Get In Touch
          </Badge>
          <h1 className="text-5xl font-bold mb-6">Contact Us</h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Have questions? We're here to help
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          <div>
            <Card className="bg-card border-border p-8 mb-8">
              <h3 className="text-2xl font-bold mb-6">Send Us a Message</h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">Name *</Label>
                    <Input 
                      id="name" 
                      value={formData.name} 
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      required
                      data-testid="contact-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input 
                      id="email" 
                      type="email"
                      value={formData.email} 
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      required
                      data-testid="contact-email"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="company">Company</Label>
                  <Input 
                    id="company" 
                    value={formData.company} 
                    onChange={(e) => setFormData({...formData, company: e.target.value})}
                    data-testid="contact-company"
                  />
                </div>
                <div>
                  <Label htmlFor="subject">Subject *</Label>
                  <Input 
                    id="subject" 
                    value={formData.subject} 
                    onChange={(e) => setFormData({...formData, subject: e.target.value})}
                    required
                    data-testid="contact-subject"
                  />
                </div>
                <div>
                  <Label htmlFor="message">Message *</Label>
                  <Textarea 
                    id="message" 
                    rows={5}
                    value={formData.message} 
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    required
                    data-testid="contact-message"
                  />
                </div>
                <Button type="submit" className="w-full btn-glow" disabled={loading} data-testid="contact-submit">
                  {loading ? "Sending..." : "Send Message"}
                </Button>
              </form>
            </Card>
          </div>

          <div>
            <Card className="bg-card border-border p-8 mb-8">
              <h3 className="text-2xl font-bold mb-6">Contact Information</h3>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-sm bg-primary/10 flex items-center justify-center shrink-0">
                    <Mail className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <p className="font-semibold">Email</p>
                    <a href="mailto:info@baltixa.com" className="text-muted-foreground hover:text-primary transition-colors">
                      info@baltixa.com
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-sm bg-primary/10 flex items-center justify-center shrink-0">
                    <Phone className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <p className="font-semibold">Phone</p>
                    <a href="tel:+905332435324" className="text-muted-foreground hover:text-primary transition-colors">
                      +90 533 243 53 24
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-sm bg-primary/10 flex items-center justify-center shrink-0">
                    <MapPin className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <p className="font-semibold">Location</p>
                    <p className="text-muted-foreground">Turkey</p>
                  </div>
                </div>
              </div>
            </Card>
            <Card className="bg-primary/5 border-primary/20 p-8">
              <h4 className="font-bold mb-3">Quick Response</h4>
              <p className="text-muted-foreground text-sm">
                We typically respond to all inquiries within 24 hours during business days. 
                For urgent matters, please indicate in your message subject.
              </p>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

// ===================== AUTH PAGES =====================

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || "/dashboard";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back!");
      navigate(from, { replace: true });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-6" data-testid="login-page">
      <Card className="w-full max-w-md bg-card border-border">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <Anchor className="w-12 h-12 text-primary" />
          </div>
          <CardTitle className="text-2xl">Welcome Back</CardTitle>
          <CardDescription>Sign in to your SpoolFix account</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input 
                id="email" 
                type="email" 
                value={email} 
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email"
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input 
                id="password" 
                type="password" 
                value={password} 
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password"
              />
            </div>
            <Button type="submit" className="w-full btn-glow" disabled={loading} data-testid="login-submit">
              {loading ? "Signing in..." : "Sign In"}
            </Button>
          </form>
          <p className="text-center text-sm text-muted-foreground mt-6">
            Don't have an account?{" "}
            <Link to="/register" className="text-primary hover:underline">Create one</Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

const RegisterPage = () => {
  const [formData, setFormData] = useState({
    email: "", password: "", company_name: "", contact_person: "", phone: ""
  });
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(formData);
      toast.success("Account created successfully!");
      navigate("/dashboard");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-6" data-testid="register-page">
      <Card className="w-full max-w-md bg-card border-border">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <Anchor className="w-12 h-12 text-primary" />
          </div>
          <CardTitle className="text-2xl">Create Account</CardTitle>
          <CardDescription>Join SpoolFix to manage your orders</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="company">Company Name *</Label>
              <Input 
                id="company" 
                value={formData.company_name} 
                onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                required
                data-testid="register-company"
              />
            </div>
            <div>
              <Label htmlFor="contact">Contact Person *</Label>
              <Input 
                id="contact" 
                value={formData.contact_person} 
                onChange={(e) => setFormData({...formData, contact_person: e.target.value})}
                required
                data-testid="register-contact"
              />
            </div>
            <div>
              <Label htmlFor="email">Email *</Label>
              <Input 
                id="email" 
                type="email" 
                value={formData.email} 
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                required
                data-testid="register-email"
              />
            </div>
            <div>
              <Label htmlFor="phone">Phone</Label>
              <Input 
                id="phone" 
                value={formData.phone} 
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                data-testid="register-phone"
              />
            </div>
            <div>
              <Label htmlFor="password">Password *</Label>
              <Input 
                id="password" 
                type="password" 
                value={formData.password} 
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                required
                minLength={6}
                data-testid="register-password"
              />
            </div>
            <Button type="submit" className="w-full btn-glow" disabled={loading} data-testid="register-submit">
              {loading ? "Creating account..." : "Create Account"}
            </Button>
          </form>
          <p className="text-center text-sm text-muted-foreground mt-6">
            Already have an account?{" "}
            <Link to="/login" className="text-primary hover:underline">Sign in</Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

// ===================== DASHBOARD PAGES =====================

const DashboardLayout = ({ children }) => {
  const { user } = useAuth();
  const location = useLocation();

  const navItems = [
    { path: "/dashboard", label: "Overview", icon: Gauge },
    { path: "/dashboard/orders", label: "My Orders", icon: FileText },
    { path: "/dashboard/new-order", label: "New Order", icon: Plus },
    { path: "/dashboard/materials", label: "Materials", icon: Package },
  ];

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar */}
          <aside className="lg:w-64 shrink-0">
            <Card className="bg-card border-border p-4 sticky top-24">
              <div className="mb-6">
                <p className="text-sm text-muted-foreground">Welcome,</p>
                <p className="font-bold truncate">{user?.contact_person}</p>
                <p className="text-sm text-muted-foreground truncate">{user?.company_name}</p>
              </div>
              <nav className="space-y-1">
                {navItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${
                      location.pathname === item.path 
                        ? "bg-primary/10 text-primary" 
                        : "text-muted-foreground hover:bg-muted hover:text-foreground"
                    }`}
                    data-testid={`nav-${item.label.toLowerCase().replace(" ", "-")}`}
                  >
                    <item.icon className="w-5 h-5" />
                    {item.label}
                  </Link>
                ))}
              </nav>
            </Card>
          </aside>

          {/* Main Content */}
          <main className="flex-1 min-w-0">
            {children}
          </main>
        </div>
      </div>
    </div>
  );
};

const DashboardOverview = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, ordersRes] = await Promise.all([
        axios.get(`${API}/stats`),
        axios.get(`${API}/orders`)
      ]);
      setStats(statsRes.data);
      setRecentOrders(ordersRes.data.slice(0, 5));
    } catch (e) {
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: "status-pending",
      in_review: "status-in_review",
      in_production: "status-in_production",
      quality_check: "status-quality_check",
      shipped: "status-shipped",
      completed: "status-completed"
    };
    return colors[status] || "bg-muted text-muted-foreground";
  };

  return (
    <DashboardLayout>
      <div data-testid="dashboard-overview">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Dashboard</h1>
          <p className="text-muted-foreground">Welcome back, {user?.contact_person}</p>
        </div>

        {/* Stats */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          {[
            { label: "Total Orders", value: stats?.total_orders || 0, icon: FileText, color: "text-primary" },
            { label: "In Progress", value: stats?.pending_orders || 0, icon: Clock, color: "text-yellow-400" },
            { label: "Completed", value: stats?.completed_orders || 0, icon: CheckCircle, color: "text-green-400" }
          ].map((stat, i) => (
            <Card key={i} className="card-industrial bg-card border-border p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">{stat.label}</p>
                  <p className="text-3xl font-bold">{loading ? "-" : stat.value}</p>
                </div>
                <stat.icon className={`w-10 h-10 ${stat.color}`} />
              </div>
            </Card>
          ))}
        </div>

        {/* Quick Actions */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <Card 
            className="bg-card border-border p-6 hover:border-primary/50 transition-colors cursor-pointer group"
            onClick={() => navigate("/dashboard/new-order")}
            data-testid="quick-new-order"
          >
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-sm bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                <Plus className="w-7 h-7 text-primary" />
              </div>
              <div>
                <h3 className="font-bold text-lg">Create New Order</h3>
                <p className="text-muted-foreground text-sm">Submit a spool fabrication request</p>
              </div>
            </div>
          </Card>
          <Card 
            className="bg-card border-border p-6 hover:border-primary/50 transition-colors cursor-pointer group"
            onClick={() => navigate("/dashboard/materials")}
            data-testid="quick-materials"
          >
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-sm bg-accent/10 flex items-center justify-center group-hover:bg-accent/20 transition-colors">
                <Package className="w-7 h-7 text-accent" />
              </div>
              <div>
                <h3 className="font-bold text-lg">Browse Materials</h3>
                <p className="text-muted-foreground text-sm">View our material catalog</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Recent Orders */}
        <Card className="bg-card border-border">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Recent Orders</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard/orders")}>
              View All <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-4">
                {[1,2,3].map(i => (
                  <div key={i} className="h-16 bg-muted animate-pulse rounded-sm"></div>
                ))}
              </div>
            ) : recentOrders.length === 0 ? (
              <div className="text-center py-8">
                <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No orders yet</p>
                <Button className="mt-4" onClick={() => navigate("/dashboard/new-order")}>
                  Create Your First Order
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {recentOrders.map((order) => (
                  <div 
                    key={order.id}
                    className="flex items-center justify-between p-4 bg-muted/30 rounded-sm hover:bg-muted/50 transition-colors cursor-pointer"
                    onClick={() => navigate(`/dashboard/orders/${order.id}`)}
                    data-testid={`order-row-${order.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-sm bg-primary/10 flex items-center justify-center">
                        <Wrench className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-semibold font-mono">{order.id}</p>
                        <p className="text-sm text-muted-foreground truncate max-w-xs">{order.title}</p>
                      </div>
                    </div>
                    <Badge className={`${getStatusColor(order.status)} border`}>
                      {order.status.replace("_", " ")}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

const OrdersListPage = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const navigate = useNavigate();

  useEffect(() => {
    fetchOrders();
  }, [filter]);

  const fetchOrders = async () => {
    try {
      const params = filter !== "all" ? `?status=${filter}` : "";
      const res = await axios.get(`${API}/orders${params}`);
      setOrders(res.data);
    } catch (e) {
      toast.error("Failed to load orders");
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: "status-pending",
      in_review: "status-in_review",
      in_production: "status-in_production",
      quality_check: "status-quality_check",
      shipped: "status-shipped",
      completed: "status-completed"
    };
    return colors[status] || "bg-muted text-muted-foreground";
  };

  return (
    <DashboardLayout>
      <div data-testid="orders-list-page">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold mb-2">My Orders</h1>
            <p className="text-muted-foreground">Track and manage your orders</p>
          </div>
          <Button className="btn-glow gap-2" onClick={() => navigate("/dashboard/new-order")} data-testid="create-order-btn">
            <Plus className="w-4 h-4" /> New Order
          </Button>
        </div>

        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {["all", "pending", "in_review", "in_production", "shipped", "completed"].map((status) => (
            <Button
              key={status}
              variant={filter === status ? "default" : "outline"}
              size="sm"
              onClick={() => setFilter(status)}
              className="shrink-0"
              data-testid={`filter-${status}`}
            >
              {status === "all" ? "All" : status.replace("_", " ")}
            </Button>
          ))}
        </div>

        {loading ? (
          <div className="space-y-4">
            {[1,2,3,4].map(i => (
              <Card key={i} className="bg-card p-6 animate-pulse">
                <div className="h-20 bg-muted rounded-sm"></div>
              </Card>
            ))}
          </div>
        ) : orders.length === 0 ? (
          <Card className="bg-card border-border text-center py-12">
            <FileText className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground mb-4">No orders found</p>
            <Button onClick={() => navigate("/dashboard/new-order")}>Create Your First Order</Button>
          </Card>
        ) : (
          <div className="space-y-4">
            {orders.map((order) => (
              <Card 
                key={order.id}
                className="bg-card border-border hover:border-primary/50 transition-colors cursor-pointer"
                onClick={() => navigate(`/dashboard/orders/${order.id}`)}
                data-testid={`order-card-${order.id}`}
              >
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-sm bg-primary/10 flex items-center justify-center shrink-0">
                        <Wrench className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <p className="font-mono font-bold text-lg">{order.id}</p>
                        <p className="font-semibold">{order.title}</p>
                        <p className="text-sm text-muted-foreground">{order.delivery_port}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-sm text-muted-foreground">
                          {new Date(order.created_at).toLocaleDateString()}
                        </p>
                        <Badge className={`${getStatusColor(order.status)} border mt-1`}>
                          {order.status.replace("_", " ")}
                        </Badge>
                      </div>
                      <ChevronRight className="w-5 h-5 text-muted-foreground" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

const OrderDetailPage = () => {
  const { id } = useLocation().pathname.split("/").pop();
  const orderId = useLocation().pathname.split("/").pop();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchOrder();
  }, [orderId]);

  const fetchOrder = async () => {
    try {
      const res = await axios.get(`${API}/orders/${orderId}`);
      setOrder(res.data);
    } catch (e) {
      toast.error("Order not found");
      navigate("/dashboard/orders");
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: "status-pending",
      in_review: "status-in_review",
      in_production: "status-in_production",
      quality_check: "status-quality_check",
      shipped: "status-shipped",
      completed: "status-completed"
    };
    return colors[status] || "bg-muted text-muted-foreground";
  };

  const getStatusProgress = (status) => {
    const statusMap = {
      pending: 10,
      in_review: 25,
      in_production: 50,
      quality_check: 75,
      shipped: 90,
      completed: 100
    };
    return statusMap[status] || 0;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="animate-pulse">
          <div className="h-10 bg-muted w-1/3 mb-6 rounded-sm"></div>
          <div className="h-64 bg-muted rounded-sm"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (!order) return null;

  return (
    <DashboardLayout>
      <div data-testid="order-detail-page">
        <Button variant="ghost" className="mb-6 gap-2" onClick={() => navigate("/dashboard/orders")}>
          <ChevronRight className="w-4 h-4 rotate-180" /> Back to Orders
        </Button>

        <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-8">
          <div>
            <p className="font-mono text-2xl font-bold text-primary">{order.id}</p>
            <h1 className="text-3xl font-bold">{order.title}</h1>
          </div>
          <Badge className={`${getStatusColor(order.status)} border text-lg px-4 py-2`}>
            {order.status.replace("_", " ")}
          </Badge>
        </div>

        {/* Progress */}
        <Card className="bg-card border-border p-6 mb-8">
          <h3 className="font-bold mb-4">Order Progress</h3>
          <Progress value={getStatusProgress(order.status)} className="h-3 mb-4" />
          <div className="flex justify-between text-sm text-muted-foreground">
            <span>Submitted</span>
            <span>Review</span>
            <span>Production</span>
            <span>QC</span>
            <span>Shipped</span>
            <span>Complete</span>
          </div>
        </Card>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Order Details */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle>Order Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Service Type</p>
                  <p className="font-semibold capitalize">{order.service_type.replace("_", " ")}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Quantity</p>
                  <p className="font-semibold">{order.quantity}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Delivery Port</p>
                  <p className="font-semibold">{order.delivery_port}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Priority</p>
                  <Badge variant={order.priority === "urgent" ? "destructive" : "outline"} className="capitalize">
                    {order.priority}
                  </Badge>
                </div>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-2">Description</p>
                <p className="text-foreground">{order.description}</p>
              </div>
              {order.specifications && (
                <div>
                  <p className="text-sm text-muted-foreground mb-2">Specifications</p>
                  <p className="text-foreground font-mono text-sm bg-muted p-3 rounded-sm">{order.specifications}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Timeline */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle>Timeline</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="relative">
                <div className="timeline-line"></div>
                <div className="space-y-6">
                  {order.timeline.map((event, i) => (
                    <div key={i} className="flex gap-4 relative">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center z-10 shrink-0 ${
                        i === order.timeline.length - 1 ? "bg-primary" : "bg-muted"
                      }`}>
                        <CheckCircle className={`w-4 h-4 ${i === order.timeline.length - 1 ? "text-primary-foreground" : "text-muted-foreground"}`} />
                      </div>
                      <div className="flex-1 pb-6">
                        <p className="font-semibold capitalize">{event.status.replace("_", " ")}</p>
                        <p className="text-sm text-muted-foreground">{event.message}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {new Date(event.timestamp).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Files */}
          <Card className="bg-card border-border lg:col-span-2">
            <CardHeader>
              <CardTitle>Attached Files</CardTitle>
            </CardHeader>
            <CardContent>
              {order.files.length === 0 ? (
                <p className="text-muted-foreground">No files attached to this order</p>
              ) : (
                <div className="grid md:grid-cols-3 gap-4">
                  {order.files.map((file) => (
                    <div key={file.id} className="flex items-center gap-3 p-3 bg-muted/30 rounded-sm">
                      <FileText className="w-8 h-8 text-primary" />
                      <div className="min-w-0">
                        <p className="font-semibold truncate">{file.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {(file.size / 1024).toFixed(1)} KB
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
};

const NewOrderPage = () => {
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    service_type: "spool_fabrication",
    specifications: "",
    quantity: 1,
    delivery_port: "",
    expected_delivery: "",
    priority: "normal"
  });
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files);
    setFiles([...files, ...newFiles]);
  };

  const removeFile = (index) => {
    setFiles(files.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Create order
      const res = await axios.post(`${API}/orders`, formData);
      const orderId = res.data.id;

      // Upload files
      for (const file of files) {
        const fileFormData = new FormData();
        fileFormData.append("file", file);
        await axios.post(`${API}/orders/${orderId}/upload`, fileFormData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
      }

      toast.success("Order created successfully!");
      navigate(`/dashboard/orders/${orderId}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create order");
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <div data-testid="new-order-page">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">New Order</h1>
          <p className="text-muted-foreground">Submit a new fabrication request</p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Order Information */}
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle>Order Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="title">Order Title *</Label>
                  <Input 
                    id="title"
                    placeholder="e.g., Engine Room Cooling Spool"
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    required
                    data-testid="order-title"
                  />
                </div>
                <div>
                  <Label htmlFor="service_type">Service Type *</Label>
                  <Select 
                    value={formData.service_type} 
                    onValueChange={(v) => setFormData({...formData, service_type: v})}
                  >
                    <SelectTrigger data-testid="order-service-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="spool_fabrication">Spool Fabrication</SelectItem>
                      <SelectItem value="raw_material">Raw Material Order</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="description">Description *</Label>
                  <Textarea 
                    id="description"
                    rows={4}
                    placeholder="Describe your requirements..."
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    required
                    data-testid="order-description"
                  />
                </div>
                <div>
                  <Label htmlFor="specifications">Technical Specifications</Label>
                  <Textarea 
                    id="specifications"
                    rows={3}
                    placeholder="Material type, dimensions, pressure rating, etc."
                    value={formData.specifications}
                    onChange={(e) => setFormData({...formData, specifications: e.target.value})}
                    className="font-mono text-sm"
                    data-testid="order-specifications"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Delivery & Priority */}
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle>Delivery Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="quantity">Quantity *</Label>
                  <Input 
                    id="quantity"
                    type="number"
                    min="1"
                    value={formData.quantity}
                    onChange={(e) => setFormData({...formData, quantity: parseInt(e.target.value) || 1})}
                    required
                    data-testid="order-quantity"
                  />
                </div>
                <div>
                  <Label htmlFor="delivery_port">Delivery Port *</Label>
                  <Input 
                    id="delivery_port"
                    placeholder="e.g., Rotterdam, Netherlands"
                    value={formData.delivery_port}
                    onChange={(e) => setFormData({...formData, delivery_port: e.target.value})}
                    required
                    data-testid="order-delivery-port"
                  />
                </div>
                <div>
                  <Label htmlFor="expected_delivery">Expected Delivery Date</Label>
                  <Input 
                    id="expected_delivery"
                    type="date"
                    value={formData.expected_delivery}
                    onChange={(e) => setFormData({...formData, expected_delivery: e.target.value})}
                    data-testid="order-expected-delivery"
                  />
                </div>
                <div>
                  <Label htmlFor="priority">Priority</Label>
                  <Select 
                    value={formData.priority} 
                    onValueChange={(v) => setFormData({...formData, priority: v})}
                  >
                    <SelectTrigger data-testid="order-priority">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="normal">Normal</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* File Upload */}
            <Card className="bg-card border-border lg:col-span-2">
              <CardHeader>
                <CardTitle>Attachments</CardTitle>
                <CardDescription>Upload sketches, drawings, or specifications</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="border-2 border-dashed border-border rounded-sm p-8 text-center hover:border-primary/50 transition-colors">
                  <input
                    type="file"
                    id="files"
                    multiple
                    onChange={handleFileChange}
                    className="hidden"
                    accept=".pdf,.png,.jpg,.jpeg,.dwg,.dxf"
                  />
                  <label htmlFor="files" className="cursor-pointer">
                    <Upload className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground mb-2">
                      Click to upload or drag and drop
                    </p>
                    <p className="text-sm text-muted-foreground">
                      PDF, PNG, JPG, DWG, DXF (max 10MB each)
                    </p>
                  </label>
                </div>
                {files.length > 0 && (
                  <div className="mt-4 space-y-2">
                    {files.map((file, i) => (
                      <div key={i} className="flex items-center justify-between p-3 bg-muted/30 rounded-sm">
                        <div className="flex items-center gap-3">
                          <FileText className="w-5 h-5 text-primary" />
                          <span className="truncate">{file.name}</span>
                          <span className="text-sm text-muted-foreground">
                            ({(file.size / 1024).toFixed(1)} KB)
                          </span>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeFile(i)}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="flex justify-end gap-4 mt-8">
            <Button type="button" variant="outline" onClick={() => navigate("/dashboard")}>
              Cancel
            </Button>
            <Button type="submit" className="btn-glow gap-2" disabled={loading} data-testid="submit-order">
              {loading ? "Submitting..." : "Submit Order"} <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
};

const DashboardMaterialsPage = () => {
  const [materials, setMaterials] = useState([]);
  const [category, setCategory] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMaterials();
  }, [category]);

  const fetchMaterials = async () => {
    try {
      const params = new URLSearchParams();
      if (category && category !== "all") params.append("category", category);
      const res = await axios.get(`${API}/materials?${params}`);
      setMaterials(res.data);
    } catch (e) {
      toast.error("Failed to load materials");
    } finally {
      setLoading(false);
    }
  };

  const filteredMaterials = materials.filter(m => 
    !search || m.name.toLowerCase().includes(search.toLowerCase()) || 
    m.material_type.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <DashboardLayout>
      <div data-testid="dashboard-materials-page">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Materials Catalog</h1>
          <p className="text-muted-foreground">Browse available materials for your orders</p>
        </div>

        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input 
              placeholder="Search materials..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="dashboard-materials-search"
            />
          </div>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="w-full md:w-48" data-testid="dashboard-materials-category">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              <SelectItem value="pipe">Pipes</SelectItem>
              <SelectItem value="flange">Flanges</SelectItem>
              <SelectItem value="elbow">Elbows</SelectItem>
              <SelectItem value="valve">Valves</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1,2,3,4,5,6].map(i => (
              <Card key={i} className="bg-card animate-pulse">
                <div className="h-40 bg-muted"></div>
                <CardContent className="p-4">
                  <div className="h-5 bg-muted rounded mb-2"></div>
                  <div className="h-4 bg-muted rounded w-2/3"></div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredMaterials.map(material => (
              <Card key={material.id} className="material-card bg-card border-border overflow-hidden">
                <div className="h-40 bg-muted relative">
                  <img 
                    src={material.image_url || "https://images.unsplash.com/photo-1590959651373-a3db0f38a961?w=400"}
                    alt={material.name}
                    className="w-full h-full object-cover"
                  />
                  <Badge className={`absolute top-2 right-2 ${
                    material.stock_status === "in_stock" ? "bg-green-500/20 text-green-400" : "bg-yellow-500/20 text-yellow-400"
                  }`}>
                    {material.stock_status === "in_stock" ? "In Stock" : "Limited"}
                  </Badge>
                </div>
                <CardContent className="p-4">
                  <Badge variant="outline" className="mb-2 uppercase text-xs">{material.category}</Badge>
                  <h3 className="font-bold mb-1">{material.name}</h3>
                  <p className="text-sm text-muted-foreground font-mono mb-2">{material.material_type}</p>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">{material.size}</span>
                    {material.price_range && (
                      <span className="text-sm font-semibold text-primary">{material.price_range}</span>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

// ===================== QUOTE CALCULATOR PAGE =====================

const QuoteCalculatorPage = () => {
  const [filters, setFilters] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedItems, setSelectedItems] = useState([]);
  const [formData, setFormData] = useState({
    contact_name: "",
    contact_email: "",
    company_name: "",
    delivery_type: "workshop",
    delivery_port: "",
    notes: ""
  });
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [quote, setQuote] = useState(null);
  const navigate = useNavigate();

  // Smart selector state
  const [selCategory, setSelCategory] = useState("");
  const [selProduct, setSelProduct] = useState("");
  const [selSchClass, setSelSchClass] = useState("");
  const [selDiameter, setSelDiameter] = useState("");
  const [availableItems, setAvailableItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [addQty, setAddQty] = useState(1);

  useEffect(() => {
    fetchFilters();
  }, []);

  const fetchFilters = async () => {
    try {
      const res = await axios.get(`${API}/pricing/filters`);
      setFilters(res.data);
    } catch (e) {
      toast.error("Failed to load filters");
    } finally {
      setLoading(false);
    }
  };

  // When category, product or schClass changes, search for items
  useEffect(() => {
    if (selCategory && selProduct) {
      const classes = getSchClasses();
      if (classes.length === 1 && classes[0] === "-") {
        // No SCH selection needed (e.g., valves) - search directly
        setSelSchClass("-");
      } else if (selSchClass && selSchClass !== "") {
        searchItems();
      } else {
        setAvailableItems([]);
      }
    } else {
      setAvailableItems([]);
      setSelSchClass("");
    }
  }, [selCategory, selProduct, selSchClass]);

  const searchItems = async () => {
    setLoadingItems(true);
    try {
      const [name, mat] = selProduct.split("|");
      const params = new URLSearchParams({ category: selCategory, name, sch_class: selSchClass });
      if (mat) params.append("material_grade", mat);
      const res = await axios.get(`${API}/pricing/search?${params}`);
      setAvailableItems(res.data);
    } catch (e) {
      setAvailableItems([]);
    } finally {
      setLoadingItems(false);
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append('file', file);
        const res = await axios.post(`${API}/upload-quote-file`, fd);
        setAttachments(prev => [...prev, { url: res.data.url, filename: res.data.filename, size: res.data.size }]);
      }
      toast.success(`${files.length} file(s) uploaded`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "File upload failed");
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const removeAttachment = (index) => setAttachments(prev => prev.filter((_, i) => i !== index));

  const addItem = (item, qty) => {
    const existing = selectedItems.find(i => i.pricing_id === item.id);
    if (existing) {
      setSelectedItems(selectedItems.map(i =>
        i.pricing_id === item.id ? { ...i, quantity: i.quantity + qty } : i
      ));
    } else {
      setSelectedItems([...selectedItems, {
        pricing_id: item.id,
        name: item.name,
        category: item.category,
        unit: item.unit,
        unit_price: item.unit_price,
        diameter: item.diameter,
        sch_class: item.sch_class,
        size_raw: item.size_raw,
        quantity: qty,
        notes: ""
      }]);
    }
    toast.success(`${item.diameter} ${item.sch_class} added`);
  };

  const updateItemQuantity = (pricingId, quantity) => {
    if (quantity <= 0) {
      setSelectedItems(selectedItems.filter(i => i.pricing_id !== pricingId));
    } else {
      setSelectedItems(selectedItems.map(i =>
        i.pricing_id === pricingId ? { ...i, quantity } : i
      ));
    }
  };

  const removeItem = (pricingId) => setSelectedItems(selectedItems.filter(i => i.pricing_id !== pricingId));

  const calculateMaterialSubtotal = () => selectedItems.reduce((sum, item) => sum + (item.unit_price * item.quantity), 0);
  const calculateWelding = () => calculateMaterialSubtotal();
  const calculateLabor = () => calculateMaterialSubtotal();
  const calculateTotal = () => calculateMaterialSubtotal() + calculateWelding() + calculateLabor();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selectedItems.length === 0) {
      toast.error("Please add at least one item");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/quotes`, {
        items: selectedItems.map(i => ({ pricing_id: i.pricing_id, quantity: i.quantity, notes: i.notes })),
        delivery_type: formData.delivery_type,
        delivery_port: formData.delivery_type === "port" ? formData.delivery_port : "",
        contact_name: formData.contact_name,
        contact_email: formData.contact_email,
        company_name: formData.company_name,
        notes: formData.notes,
        attachments: attachments.map(a => a.url)
      });
      setQuote(res.data);
      toast.success("Quote generated!");
    } catch (e) {
      toast.error("Failed to generate quote");
    } finally {
      setSubmitting(false);
    }
  };

  const getUnitLabel = (unit) => ({ per_meter: "/m", per_piece: "/pc" }[unit] || unit);

  const getCategoryLabel = (cat) => ({ pipe: "Pipes", flange: "Flanges", elbow: "Elbows", valve: "Valves" }[cat] || cat);

  const getProducts = () => {
    if (!selCategory || !filters[selCategory]) return [];
    return filters[selCategory];
  };

  const getSchClasses = () => {
    const product = getProducts().find(p => `${p.name}|${p.material_grade}` === selProduct || p.name === selProduct);
    return product ? product.sch_classes : [];
  };

  // Parse diameter to numeric for sorting
  const parseDiameter = (d) => {
    if (!d) return 999;
    const clean = d.replace('"', '').trim();
    if (clean.includes(' ')) {
      const [whole, frac] = clean.split(' ');
      const [num, den] = frac.split('/').map(Number);
      return Number(whole) + (num / den);
    }
    if (clean.includes('/')) {
      const [num, den] = clean.split('/').map(Number);
      return num / den;
    }
    return Number(clean) || 999;
  };

  const sortedAvailableItems = [...availableItems].sort((a, b) => parseDiameter(a.diameter) - parseDiameter(b.diameter));

  if (quote) {
    return (
      <div className="min-h-screen py-24" data-testid="quote-result-page">
        <div className="max-w-4xl mx-auto px-6">
          <Card className="bg-card border-border">
            <CardHeader className="text-center border-b border-border">
              <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
              <CardTitle className="text-3xl">Quote Generated</CardTitle>
              <CardDescription>Reference: <span className="font-mono text-primary">{quote.id}</span></CardDescription>
            </CardHeader>
            <CardContent className="p-8">
              <div className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <p className="text-sm text-muted-foreground">Contact</p>
                    <p className="font-semibold">{quote.contact_name}</p>
                    <p className="text-sm text-muted-foreground">{quote.contact_email}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Delivery</p>
                    <p className="font-semibold">
                      {quote.delivery_type === "workshop" ? "Workshop Delivery" : `Port: ${quote.delivery_port}`}
                    </p>
                  </div>
                </div>

                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Item</TableHead>
                      <TableHead className="text-right">Unit Price</TableHead>
                      <TableHead className="text-right">Qty</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {quote.items.map((item, i) => (
                      <TableRow key={i}>
                        <TableCell>
                          <p className="font-medium">{item.name}</p>
                          <p className="text-sm text-muted-foreground">{item.size_raw || item.category}</p>
                        </TableCell>
                        <TableCell className="text-right font-mono">${item.unit_price.toFixed(2)}</TableCell>
                        <TableCell className="text-right">{item.quantity}</TableCell>
                        <TableCell className="text-right font-mono font-semibold">${item.line_total.toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                <div className="border-t border-border pt-4 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Material Subtotal</span>
                    <span className="font-mono">${quote.material_subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Welding</span>
                    <span className="font-mono">${quote.welding_total.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Labor</span>
                    <span className="font-mono">${quote.labor_total.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Transport</span>
                    <span className="font-mono">
                      {quote.delivery_type === "workshop" ? "$0.00 (Workshop)" : "To be quoted"}
                    </span>
                  </div>
                  {quote.delivery_note && (
                    <div className="bg-amber-500/10 border border-amber-500/30 rounded-sm p-3">
                      <p className="text-sm text-amber-600">{quote.delivery_note}</p>
                    </div>
                  )}
                  <div className="flex justify-between text-xl font-bold pt-2 border-t border-border">
                    <span>Total Estimate</span>
                    <span className="text-primary font-mono">${quote.total.toFixed(2)}</span>
                  </div>
                </div>

                <div className="bg-primary/5 border border-primary/20 rounded-sm p-4">
                  <p className="text-sm text-muted-foreground">
                  This is a preliminary estimate. Final pricing may vary based on detailed specifications, 
                    material availability, and delivery requirements. Our team will contact you within 24 hours.
                  </p>
                </div>

                <div className="flex gap-4">
                  <Button onClick={() => { setQuote(null); setSelectedItems([]); }} variant="outline" className="flex-1">New Quote</Button>
                  <Button onClick={() => navigate("/register")} className="flex-1 btn-glow">Create Account to Order</Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-24" data-testid="quote-calculator-page">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-12">
          <Badge className="mb-4 bg-primary/20 text-primary border-primary/30 uppercase tracking-wider">Instant Estimate</Badge>
          <h1 className="text-5xl font-bold mb-6">Quote Calculator</h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Get a preliminary price estimate for your pipe spool fabrication project
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Smart Product Selector */}
          <div className="lg:col-span-2 space-y-6">
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle>Product Selection</CardTitle>
                <CardDescription>Add products by selecting category, type, standard and diameter</CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="h-12 bg-muted animate-pulse rounded-sm"></div>)}</div>
                ) : (
                  <div className="space-y-4">
                    {/* Row 1: Category & Product */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs text-muted-foreground mb-1 block">1. Category</Label>
                        <Select value={selCategory} onValueChange={(v) => { setSelCategory(v); setSelProduct(""); setSelSchClass(""); setSelDiameter(""); }}>
                          <SelectTrigger data-testid="sel-category"><SelectValue placeholder="Select category" /></SelectTrigger>
                          <SelectContent>
                            {Object.keys(filters).map(cat => (
                              <SelectItem key={cat} value={cat}>{getCategoryLabel(cat)}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground mb-1 block">2. Product Type</Label>
                        <Select value={selProduct} onValueChange={(v) => { setSelProduct(v); setSelSchClass(""); setSelDiameter(""); }} disabled={!selCategory}>
                          <SelectTrigger data-testid="sel-product"><SelectValue placeholder="Select product" /></SelectTrigger>
                          <SelectContent>
                            {getProducts().map((p, i) => (
                              <SelectItem key={i} value={`${p.name}|${p.material_grade}`}>
                                {p.name} <span className="text-muted-foreground text-xs ml-1">({p.material_grade})</span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Row 2: SCH/Standard */}
                    {selProduct && (
                      <div>
                        <Label className="text-xs text-muted-foreground mb-1 block">3. Standard / SCH Value</Label>
                        <div className="flex flex-wrap gap-2">
                          {getSchClasses().map(sch => (
                            <Button
                              key={sch}
                              type="button"
                              size="sm"
                              variant={selSchClass === sch ? "default" : "outline"}
                              onClick={() => { setSelSchClass(sch); setSelDiameter(""); }}
                              data-testid={`sch-${sch}`}
                              className={selSchClass === sch ? "btn-glow" : ""}
                            >
                              {sch}
                            </Button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Row 3: Size/Diameter list */}
                    {selSchClass && (
                      <div>
                        <Label className="text-xs text-muted-foreground mb-1 block">4. Select Diameter & Add</Label>
                        {loadingItems ? (
                          <div className="h-40 bg-muted animate-pulse rounded-sm"></div>
                        ) : (
                          <div className="border border-border rounded-sm max-h-80 overflow-y-auto">
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>Diameter</TableHead>
                                  <TableHead>Details</TableHead>
                                  <TableHead className="text-right">Price</TableHead>
                                  <TableHead className="text-right w-32">Qty</TableHead>
                                  <TableHead className="text-right w-20"></TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {sortedAvailableItems.map(item => (
                                  <TableRow key={item.id} className="hover:bg-muted/30" data-testid={`size-row-${item.id}`}>
                                    <TableCell className="font-semibold">{item.diameter}</TableCell>
                                    <TableCell className="text-sm text-muted-foreground">
                                      {item.od && `${item.od} `}{item.dn && item.dn}
                                    </TableCell>
                                    <TableCell className="text-right font-mono text-primary font-bold">
                                      ${item.unit_price.toFixed(2)}<span className="text-muted-foreground text-xs">{getUnitLabel(item.unit)}</span>
                                    </TableCell>
                                    <TableCell className="text-right">
                                      <Input
                                        type="number"
                                        min="0.1"
                                        step="0.1"
                                        defaultValue="1"
                                        className="w-20 text-center inline-block"
                                        data-testid={`qty-${item.id}`}
                                        id={`qty-${item.id}`}
                                      />
                                    </TableCell>
                                    <TableCell className="text-right">
                                      <Button
                                        size="sm"
                                        className="btn-glow"
                                        data-testid={`add-${item.id}`}
                                        onClick={() => {
                                          const qtyInput = document.getElementById(`qty-${item.id}`);
                                          const qty = parseFloat(qtyInput?.value) || 1;
                                          addItem(item, qty);
                                        }}
                                      >
                                        <Plus className="w-4 h-4" />
                                      </Button>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Quote Summary */}
          <div>
            <Card className="bg-card border-border sticky top-24">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calculator className="w-5 h-5" /> Your Quote
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  {selectedItems.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Calculator className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>Add items from the left panel</p>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {selectedItems.map((item) => (
                        <div key={item.pricing_id} className="flex items-center justify-between p-2 bg-muted/30 rounded-sm text-sm">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">{item.name}</p>
                            <p className="text-xs text-muted-foreground">{item.diameter} {item.sch_class} - ${item.unit_price}{getUnitLabel(item.unit)}</p>
                          </div>
                          <div className="flex items-center gap-1">
                            <Button type="button" size="sm" variant="outline" className="h-7 w-7 p-0" onClick={() => updateItemQuantity(item.pricing_id, item.quantity - 1)}>-</Button>
                            <Input
                              type="number"
                              value={item.quantity}
                              onChange={(e) => updateItemQuantity(item.pricing_id, parseFloat(e.target.value) || 0)}
                              className="w-14 h-7 text-center text-sm"
                              min="0" step="0.1"
                            />
                            <Button type="button" size="sm" variant="outline" className="h-7 w-7 p-0" onClick={() => updateItemQuantity(item.pricing_id, item.quantity + 1)}>+</Button>
                            <Button type="button" size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => removeItem(item.pricing_id)}>
                              <Trash2 className="w-3 h-3 text-destructive" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {selectedItems.length > 0 && (
                    <>
                      <div className="border-t border-border pt-3 space-y-1 text-sm">
                        <div className="flex justify-between"><span className="text-muted-foreground">Material</span><span className="font-mono">${calculateMaterialSubtotal().toFixed(2)}</span></div>
                        <div className="flex justify-between"><span className="text-muted-foreground">Welding</span><span className="font-mono">${calculateWelding().toFixed(2)}</span></div>
                        <div className="flex justify-between"><span className="text-muted-foreground">Labor</span><span className="font-mono">${calculateLabor().toFixed(2)}</span></div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Transport</span>
                          <span className="font-mono">{formData.delivery_type === "workshop" ? "$0.00" : "To be quoted"}</span>
                        </div>
                        <div className="flex justify-between text-lg font-bold pt-2 border-t border-border">
                          <span>Total</span>
                          <span className="font-mono text-primary">${calculateTotal().toFixed(2)}</span>
                        </div>
                      </div>

                      <div className="space-y-3 border-t border-border pt-3">
                        <div>
                          <Label className="text-xs">Delivery Type *</Label>
                          <Select value={formData.delivery_type} onValueChange={(v) => setFormData({...formData, delivery_type: v})}>
                            <SelectTrigger data-testid="quote-delivery-type"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="workshop">Workshop Delivery (Free)</SelectItem>
                              <SelectItem value="port">Port / Other Location</SelectItem>
                            </SelectContent>
                          </Select>
                          {formData.delivery_type === "port" && (
                            <p className="text-xs text-amber-600 mt-1">Delivery price will be provided upon inquiry</p>
                          )}
                        </div>
                        {formData.delivery_type === "port" && (
                          <div>
                            <Label className="text-xs">Delivery Address</Label>
                            <Input placeholder="e.g., Istanbul, Rotterdam, Hamburg" value={formData.delivery_port}
                              onChange={(e) => setFormData({...formData, delivery_port: e.target.value})} data-testid="quote-delivery-port" />
                          </div>
                        )}
                        <div>
                          <Label className="text-xs">Your Name *</Label>
                          <Input value={formData.contact_name} onChange={(e) => setFormData({...formData, contact_name: e.target.value})} required data-testid="quote-contact-name" />
                        </div>
                        <div>
                          <Label className="text-xs">Email *</Label>
                          <Input type="email" value={formData.contact_email} onChange={(e) => setFormData({...formData, contact_email: e.target.value})} required data-testid="quote-contact-email" />
                        </div>
                        <div>
                          <Label className="text-xs">Company</Label>
                          <Input value={formData.company_name} onChange={(e) => setFormData({...formData, company_name: e.target.value})} data-testid="quote-company-name" />
                        </div>
                        <div>
                          <Label className="text-xs">Additional Notes</Label>
                          <Textarea rows={2} value={formData.notes} onChange={(e) => setFormData({...formData, notes: e.target.value})} data-testid="quote-notes" />
                        </div>
                        <div>
                          <Label className="text-xs">Attachments (Technical drawings, etc.)</Label>
                          <div className="mt-1 border-2 border-dashed border-border rounded-lg p-3 text-center">
                            <input type="file" multiple accept=".pdf,.dwg,.dxf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.zip"
                              onChange={handleFileUpload} className="hidden" id="file-upload" disabled={uploading} />
                            <label htmlFor="file-upload" className="cursor-pointer">
                              <Upload className="w-6 h-6 mx-auto text-muted-foreground mb-1" />
                              <p className="text-xs text-muted-foreground">{uploading ? "Uploading..." : "Click to upload files"}</p>
                            </label>
                          </div>
                          {attachments.length > 0 && (
                            <div className="mt-2 space-y-1">
                              {attachments.map((file, index) => (
                                <div key={index} className="flex items-center justify-between p-1.5 bg-muted/50 rounded text-xs">
                                  <div className="flex items-center gap-1">
                                    <FileText className="w-3 h-3 text-sky-400" />
                                    <span>{file.filename}</span>
                                  </div>
                                  <Button type="button" variant="ghost" size="sm" onClick={() => removeAttachment(index)} className="h-5 w-5 p-0 text-red-400">
                                    <X className="w-3 h-3" />
                                  </Button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>

                      <Button type="submit" className="w-full btn-glow" disabled={submitting} data-testid="quote-submit">
                        {submitting ? "Calculating..." : "Get Quote"}
                      </Button>
                    </>
                  )}
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

// ===================== ADMIN PRICING PAGE =====================

const AdminPricingPage = () => {
  const { isAdmin, adminLogin, adminLogout, getAdminHeaders } = useAdmin();
  const [password, setPassword] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [pricing, setPricing] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editItem, setEditItem] = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [formData, setFormData] = useState({
    category: "pipe",
    name: "",
    unit: "per_piece",
    unit_price: 0,
    material_grade: "",
    size_range: "",
    description: ""
  });

  useEffect(() => {
    if (isAdmin) {
      fetchPricing();
    } else {
      setLoading(false);
    }
  }, [isAdmin]);

  const fetchPricing = async () => {
    try {
      const res = await axios.get(`${API}/pricing?active_only=false`, { headers: getAdminHeaders() });
      setPricing(res.data);
    } catch (e) {
      toast.error("Failed to load pricing");
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    try {
      await adminLogin(password);
      toast.success("Admin login successful");
      setPassword("");
      fetchPricing();
    } catch (e) {
      toast.error("Invalid admin password");
    } finally {
      setLoginLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      if (editItem) {
        await axios.patch(`${API}/pricing/${editItem.id}`, formData, { headers: getAdminHeaders() });
        toast.success("Pricing updated");
      } else {
        await axios.post(`${API}/pricing`, formData, { headers: getAdminHeaders() });
        toast.success("Pricing item added");
      }
      setEditItem(null);
      setShowAddDialog(false);
      setFormData({ category: "pipe", name: "", unit: "per_piece", unit_price: 0, material_grade: "", size_range: "", description: "" });
      fetchPricing();
    } catch (e) {
      toast.error("Failed to save pricing");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this pricing item?")) return;
    try {
      await axios.delete(`${API}/pricing/${id}`, { headers: getAdminHeaders() });
      toast.success("Pricing item deleted");
      fetchPricing();
    } catch (e) {
      toast.error("Failed to delete pricing item");
    }
  };

  const openEditDialog = (item) => {
    setEditItem(item);
    setFormData({
      category: item.category,
      name: item.name,
      unit: item.unit,
      unit_price: item.unit_price,
      material_grade: item.material_grade || "",
      size_range: item.size_range || "",
      description: item.description || ""
    });
    setShowAddDialog(true);
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen py-24" data-testid="admin-login-page">
        <div className="max-w-md mx-auto px-6">
          <Card className="bg-card border-border">
            <CardHeader className="text-center">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <Lock className="w-8 h-8 text-primary" />
              </div>
              <CardTitle>Admin Access</CardTitle>
              <CardDescription>Enter admin password to manage pricing</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <Label htmlFor="admin-password">Password</Label>
                  <Input
                    id="admin-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    data-testid="admin-password"
                  />
                </div>
                <Button type="submit" className="w-full btn-glow" disabled={loginLoading} data-testid="admin-login-btn">
                  {loginLoading ? "Logging in..." : "Login"}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-24" data-testid="admin-pricing-page">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex justify-between items-center mb-8">
          <div>
            <Badge className="mb-2 bg-accent/20 text-accent border-accent/30 uppercase tracking-wider">
              Admin Panel
            </Badge>
            <h1 className="text-4xl font-bold">Pricing Management</h1>
          </div>
          <div className="flex gap-4">
            <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
              <DialogTrigger asChild>
                <Button className="btn-glow gap-2" onClick={() => { setEditItem(null); setFormData({ category: "pipe", name: "", unit: "per_piece", unit_price: 0, material_grade: "", size_range: "", description: "" }); }}>
                  <Plus className="w-4 h-4" /> Add Item
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-card border-border">
                <DialogHeader>
                  <DialogTitle>{editItem ? "Edit Pricing" : "Add Pricing Item"}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Category</Label>
                      <Select value={formData.category} onValueChange={(v) => setFormData({...formData, category: v})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pipe">Pipe</SelectItem>
                          <SelectItem value="flange">Flange</SelectItem>
                          <SelectItem value="elbow">Elbow</SelectItem>
                          <SelectItem value="valve">Valve</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Unit</Label>
                      <Select value={formData.unit} onValueChange={(v) => setFormData({...formData, unit: v})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="per_meter">Per Meter</SelectItem>
                          <SelectItem value="per_piece">Per Piece</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label>Name *</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label>Unit Price ($) *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.unit_price}
                      onChange={(e) => setFormData({...formData, unit_price: parseFloat(e.target.value) || 0})}
                      required
                    />
                  </div>
                  <div>
                    <Label>Material Grade</Label>
                    <Input
                      value={formData.material_grade}
                      onChange={(e) => setFormData({...formData, material_grade: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Size Range</Label>
                    <Input
                      value={formData.size_range}
                      onChange={(e) => setFormData({...formData, size_range: e.target.value})}
                      placeholder='e.g., 2"-6"'
                    />
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Textarea
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                    />
                  </div>
                  <Button onClick={handleSave} className="w-full btn-glow">
                    <Save className="w-4 h-4 mr-2" /> {editItem ? "Update" : "Add"} Pricing
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            <Button variant="outline" onClick={adminLogout}>
              <LogOut className="w-4 h-4 mr-2" /> Logout
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="space-y-4">
            {[1,2,3,4].map(i => <div key={i} className="h-16 bg-muted animate-pulse rounded-sm"></div>)}
          </div>
        ) : (
          <Card className="bg-card border-border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Category</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Material</TableHead>
                  <TableHead>Size Range</TableHead>
                  <TableHead className="text-right">Unit Price</TableHead>
                  <TableHead>Unit</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pricing.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell className="capitalize">{item.category}</TableCell>
                    <TableCell className="font-medium">{item.name}</TableCell>
                    <TableCell className="text-muted-foreground">{item.material_grade || "-"}</TableCell>
                    <TableCell className="text-muted-foreground">{item.size_range || "-"}</TableCell>
                    <TableCell className="text-right font-mono font-bold text-primary">${item.unit_price.toFixed(2)}</TableCell>
                    <TableCell className="text-muted-foreground capitalize">{item.unit.replace("_", " ")}</TableCell>
                    <TableCell>
                      <Badge className={item.is_active ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"}>
                        {item.is_active ? "Active" : "Inactive"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button size="sm" variant="ghost" onClick={() => openEditDialog(item)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDelete(item.id)}>
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        )}
      </div>
    </div>
  );
};

// ===================== MAIN APP =====================

// Visitor Tracking
const VisitorTracker = () => {
  const location = useLocation();
  useEffect(() => {
    axios.post(`${API}/track-visit`, { page: location.pathname }).catch(() => {});
  }, [location.pathname]);
  return null;
};

function App() {
  return (
    <AuthProvider>
      <AdminProvider>
        <TeamAdminProvider>
          <BrowserRouter>
            <div className="min-h-screen flex flex-col bg-background">
              <VisitorTracker />
              <Navigation />
              <main className="flex-1">
                <Routes>
                  {/* Public Routes */}
                  <Route path="/" element={<HomePage />} />
                  <Route path="/services" element={<ServicesPage />} />
                  <Route path="/materials" element={<MaterialsPage />} />
                  <Route path="/how-it-works" element={<HowItWorksPage />} />
                  <Route path="/about" element={<AboutPage />} />
                  <Route path="/contact" element={<ContactPage />} />
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/register" element={<RegisterPage />} />
                  <Route path="/quote-calculator" element={<QuoteCalculatorPage />} />
                  <Route path="/admin/pricing" element={<AdminPricingPage />} />
                  
                  {/* Team Admin Routes */}
                  <Route path="/team-admin/login" element={<TeamAdminLoginPage />} />
                  <Route path="/team-admin" element={<TeamAdminDashboard />} />
                  
                  {/* Protected Routes */}
                  <Route path="/dashboard" element={<ProtectedRoute><DashboardOverview /></ProtectedRoute>} />
                  <Route path="/dashboard/orders" element={<ProtectedRoute><OrdersListPage /></ProtectedRoute>} />
                  <Route path="/dashboard/orders/:id" element={<ProtectedRoute><OrderDetailPage /></ProtectedRoute>} />
                  <Route path="/dashboard/new-order" element={<ProtectedRoute><NewOrderPage /></ProtectedRoute>} />
                  <Route path="/dashboard/materials" element={<ProtectedRoute><DashboardMaterialsPage /></ProtectedRoute>} />
                  
                  {/* Catch all */}
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </main>
              <Footer />
              <WhatsAppButton />
              <Toaster position="top-right" richColors />
            </div>
          </BrowserRouter>
        </TeamAdminProvider>
      </AdminProvider>
    </AuthProvider>
  );
}

export default App;
